import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  MessageSquare,
  Instagram,
  Target,
  Bot,
  Sparkles,
  LineChart,
  Layout as LayoutIcon,
  UserSquare2,
  Volume2,
  VolumeX,
  MapPin,
} from "lucide-react";
import Logo from "@/components/seven/Logo";
import CursorFX from "@/components/seven/CursorFX";
import Reveal from "@/components/seven/Reveal";
import Counter from "@/components/seven/Counter";
import MayaChat from "@/components/seven/MayaChat";
import Marquee from "@/components/seven/Marquee";

const WHATSAPP_URL = "https://wa.me/5561981105746";
const INSTAGRAM_URL = "https://www.instagram.com/7impulsegrowth?igsh=b2dkbDg0NGxzcDEx";
const FOUNDERS_IMG =
  "https://customer-assets.emergentagent.com/job_f62b53de-ac3f-4ad1-a2cb-8345ae8b2cdc/artifacts/wt1nw5oz_Marcusejunews.png";
const CLONE_VIDEO = "/videos/clone-ia.mp4";

/* -------------------- Floating top bar -------------------- */
const TopBar = () => (
  <header
    className="fixed top-3 sm:top-5 inset-x-0 z-40 flex justify-center px-3 sm:px-6"
    data-testid="top-bar"
  >
    <div className="w-full max-w-6xl flex items-center justify-between rounded-full border border-[rgba(197,212,181,0.14)] bg-[rgba(10,10,10,0.65)] backdrop-blur-xl px-3.5 sm:px-5 py-2.5">
      <a
        href="#top"
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        data-testid="top-bar-logo"
      >
        <Logo size={22} />
      </a>
      <div className="flex items-center gap-2 sm:gap-3">
        <Link
          to="/portfolio"
          className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(245,241,232,0.18)] text-[#F5F1E8] px-3 sm:px-3.5 py-2 text-[12px] sm:text-[12.5px] font-medium hover:border-[#C5D4B5] hover:text-[#C5D4B5] transition"
          data-testid="top-bar-portfolio-link"
          data-cursor="hover"
        >
          Portfólio
          <ArrowUpRight size={13} />
        </Link>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-3.5 sm:px-4 py-2 text-[12.5px] sm:text-[13px] font-medium hover:brightness-95 transition-all"
          data-testid="top-bar-whatsapp-btn"
          data-cursor="hover"
        >
          <MessageSquare size={14} strokeWidth={2.4} />
          <span className="hidden sm:inline">Quero vender mais</span>
          <span className="sm:hidden">Vender mais</span>
          <ArrowRight
            size={14}
            className="group-hover:translate-x-0.5 transition-transform"
          />
        </a>
      </div>
    </div>
  </header>
);

/* -------------------- Hero -------------------- */
const Hero = () => (
  <section
    id="top"
    className="relative min-h-[100svh] w-full overflow-hidden bg-radial-hero bg-grid pt-28 sm:pt-36 pb-16 sm:pb-24"
    data-testid="hero-section"
  >
    <div className="aurora" aria-hidden="true" />

    <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="mono inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#C5D4B5]/90 mb-7">
          <span className="h-px w-8 bg-[#C5D4B5]/50" />
          Seven Impulse · Brasília–DF
        </span>
      </Reveal>

      <Reveal delay={80}>
        <h1
          className="text-[44px] sm:text-6xl lg:text-[80px] leading-[0.95] tracking-[-0.025em] font-medium text-[#F5F1E8] max-w-5xl"
          data-testid="hero-headline"
        >
          Transformamos as redes sociais no{" "}
          <span className="text-[#C5D4B5]">maior canal de vendas</span> do seu
          negócio.
        </h1>
      </Reveal>

      <Reveal delay={180}>
        <p
          className="mt-6 sm:mt-8 max-w-2xl text-[15px] sm:text-[17px] leading-relaxed text-[#F5F1E8]/70"
          data-testid="hero-subheadline"
        >
          Unimos conteúdo orgânico, tráfego pago, IA e automação para
          transformar o seu negócio numa máquina de atrair e fidelizar
          clientes.
        </p>
      </Reveal>

      <Reveal delay={260}>
        <div className="mt-9 sm:mt-11 flex flex-wrap items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-5 py-3 text-[14px] font-medium hover:brightness-95 transition sage-glow-soft"
            data-testid="hero-primary-cta"
            data-cursor="hover"
          >
            Quero crescer com IA
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform"
            />
          </a>
          <a
            href="#services"
            className="group inline-flex items-center gap-2 rounded-full border border-[rgba(197,212,181,0.25)] text-[#F5F1E8] px-5 py-3 text-[14px] font-medium hover:border-[#C5D4B5] hover:text-[#C5D4B5] transition"
            data-testid="hero-secondary-cta"
            data-cursor="hover"
          >
            Ver serviços
            <ArrowUpRight
              size={16}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </a>
        </div>
      </Reveal>

      {/* Inline mini meta row */}
      <Reveal delay={360}>
        <div className="mt-14 sm:mt-20 grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-8 max-w-3xl">
          {[
            ["34+", "clientes atendidos"],
            ["R$ 2.3M+", "geridos em mídia"],
            ["24/7", "operação com IA"],
            ["Brasil", "clientes em todo o país"],
          ].map(([n, l]) => (
            <div key={l} className="flex flex-col">
              <span className="text-[#C5D4B5] text-[22px] sm:text-[26px] font-medium tracking-tight">
                {n}
              </span>
              <span className="mono text-[10.5px] uppercase tracking-[0.18em] text-[#F5F1E8]/50">
                {l}
              </span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>

    {/* Social proof ticker */}
    <div className="relative mt-16 sm:mt-24 border-y border-[rgba(197,212,181,0.12)] py-4 bg-[rgba(197,212,181,0.02)]">
      <Marquee
        testid="hero-ticker"
        items={[
          "34+ clientes atendidos",
          "Clientes em todo o Brasil",
          "Brasília–DF",
          "Meta",
          "Google",
          "Implementação operacional com IA",
          "Clone Digital",
        ]}
      />
    </div>
  </section>
);

/* -------------------- Services Section -------------------- */
const services = [
  {
    icon: Target,
    title: "Tráfego Pago",
    subtitle: "META ADS · GOOGLE ADS",
    description:
      "Aquisição escalável de clientes com campanhas otimizadas por IA, segmentação cirúrgica e CAC previsível.",
    tags: ["ROAS", "FULL FUNNEL", "RETARGETING"],
  },
  {
    icon: Bot,
    title: "Automações com IA",
    subtitle: "ATENDIMENTO · CAPTAÇÃO · PRODUTIVIDADE",
    description:
      "Soluções para atendimento, WhatsApp, captação, organização comercial e aumento de produtividade.",
    tags: ["IA", "WHATSAPP", "CRM"],
  },
  {
    icon: MapPin,
    title: "Google Business e presença local",
    subtitle: "GOOGLE · MAPS · SEO LOCAL",
    description:
      "Estratégias para empresas locais aparecerem melhor, venderem mais e serem encontradas com facilidade.",
    tags: ["GMB", "MAPS", "SEO LOCAL"],
  },
  {
    icon: Sparkles,
    title: "Conteúdo & Social",
    subtitle: "INSTAGRAM · TIKTOK · YOUTUBE",
    description:
      "Posicionamento premium e produção contínua que transforma atenção em audiência qualificada.",
    tags: ["REELS", "UGC", "AUTORIDADE"],
  },
  {
    icon: LineChart,
    title: "Estratégia de Marketing",
    subtitle: "PLANEJAMENTO · DADOS",
    description:
      "Planejamento focado em ROI, LTV e crescimento previsível com métricas que importam para o caixa.",
    tags: ["FUNIL", "DASHBOARDS", "LTV"],
  },
  {
    icon: LayoutIcon,
    title: "Criação de Sites",
    subtitle: "DESIGN · CONVERSÃO · PERFORMANCE",
    description:
      "Sites modernos, rápidos e pensados para converter visitantes em clientes. Do layout à integração com automação.",
    tags: ["LANDING PAGE", "SEO", "IA INTEGRADA"],
  },
];

/* -------------------- Clone IA Video with sound toggle -------------------- */
const CloneVideo = () => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    if (!next) {
      // unmuting — ensure it plays
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    setMuted(next);
  };

  return (
    <div className="relative w-full rounded-xl overflow-hidden sage-glow-soft border border-[rgba(197,212,181,0.18)] bg-[#050505] aspect-video max-h-[640px] mx-auto">
      <video
        ref={ref}
        src={CLONE_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-contain"
        data-testid="clone-video"
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 0%, transparent 50%, rgba(10,10,10,0.55) 100%)",
        }}
      />
      <span className="absolute top-3 left-3 mono text-[10px] tracking-[0.22em] uppercase text-[#C5D4B5] bg-[rgba(10,10,10,0.7)] backdrop-blur px-2 py-1 rounded">
        ● Ao vivo · Avatar IA
      </span>
      <button
        type="button"
        onClick={toggle}
        aria-label={muted ? "Ativar áudio" : "Silenciar"}
        className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-3 py-2 text-[11px] mono tracking-[0.18em] uppercase hover:brightness-95 transition sage-glow-soft"
        data-testid="clone-video-sound-toggle"
        data-cursor="hover"
      >
        {muted ? <VolumeX size={14} strokeWidth={2.2} /> : <Volume2 size={14} strokeWidth={2.2} />}
        {muted ? "Ativar som" : "Silenciar"}
      </button>
    </div>
  );
};

const ServiceCard = ({ s, index }) => (
  <Reveal delay={index * 70}>
    <div
      className="group relative h-full rounded-2xl border border-[rgba(197,212,181,0.12)] bg-[#0E0E0E] p-6 sm:p-7 transition-all duration-500 hover:border-[rgba(197,212,181,0.4)] hover:-translate-y-1 hover:bg-[#101010]"
      data-cursor="hover"
      data-testid={`service-card-${index}`}
    >
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
           style={{
             background:
               "radial-gradient(400px circle at var(--mx,50%) var(--my,50%), rgba(197,212,181,0.05), transparent 60%)",
           }}
      />
      <div className="relative flex flex-col h-full">
        <div className="flex items-start justify-between mb-7">
          <div className="h-10 w-10 rounded-xl bg-[rgba(197,212,181,0.08)] border border-[rgba(197,212,181,0.16)] flex items-center justify-center text-[#C5D4B5] group-hover:bg-[rgba(197,212,181,0.16)] transition-colors">
            <s.icon size={18} strokeWidth={1.6} />
          </div>
          <ArrowUpRight
            size={16}
            className="text-[#F5F1E8]/40 group-hover:text-[#C5D4B5] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
          />
        </div>

        <span className="mono text-[10.5px] tracking-[0.22em] text-[#C5D4B5]/80 uppercase mb-3">
          {s.subtitle}
        </span>
        <h3 className="text-[22px] sm:text-[24px] tracking-tight text-[#F5F1E8] font-medium mb-3">
          {s.title}
        </h3>
        <p className="text-[14px] leading-relaxed text-[#F5F1E8]/65 mb-7">
          {s.description}
        </p>

        <div className="mt-auto pt-5 border-t border-[rgba(197,212,181,0.08)] flex flex-wrap gap-2">
          {s.tags.map((t) => (
            <span
              key={t}
              className="mono text-[10px] tracking-[0.16em] text-[#F5F1E8]/55 uppercase px-2 py-1 rounded-md bg-[rgba(245,241,232,0.04)]"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  </Reveal>
);

const Services = () => (
  <section
    id="services"
    className="relative py-24 sm:py-32 border-t border-[rgba(197,212,181,0.08)]"
    data-testid="services-section"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
          / Serviços
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-5 text-[34px] sm:text-5xl lg:text-[56px] leading-[1.02] tracking-[-0.02em] font-medium text-[#F5F1E8] max-w-4xl">
          Múltiplas frentes. Um único objetivo:{" "}
          <span className="text-[#C5D4B5]">crescimento previsível.</span>
        </h2>
      </Reveal>

      <div className="mt-14 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {services.map((s, i) => (
          <ServiceCard key={s.title} s={s} index={i} />
        ))}

        {/* Featured Card 6 — Clone IA video only */}
        <Reveal delay={services.length * 60} className="sm:col-span-2 lg:col-span-3">
          <div
            className="relative rounded-2xl border border-[rgba(197,212,181,0.18)] bg-[#080808] overflow-hidden group hover:border-[rgba(197,212,181,0.45)] transition-colors p-4 sm:p-6"
            data-testid="service-card-clone"
            data-cursor="hover"
          >
            <div className="relative w-full max-w-4xl mx-auto">
              <CloneVideo />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* -------------------- Cases -------------------- */
const cases = [
  {
    big: "87%",
    label: "redução no tempo de resposta ao cliente",
    sector: "Clínica de Fisioterapia",
    desc:
      "Agente de IA no WhatsApp fazendo triagem, agendamento, confirmações e pós-consulta 24/7. Liberando a recepção para relacionamento de alto valor.",
  },
  {
    big: "3.2×",
    label: "mais leads qualificados",
    sector: "Escritório de Advocacia",
    desc:
      "Tráfego pago com segmentação por intenção e qualificação automatizada antes do primeiro contato humano.",
  },
  {
    big: "45%",
    label: "aumento em orçamentos fechados",
    sector: "Decoração de Festas",
    desc:
      "Funil de Meta Ads conectado a follow up inteligente no WhatsApp. Conversão sem perder o lead na mesa.",
  },
  {
    big: "2.7×",
    label: "retorno sobre investimento",
    sector: "Ótica",
    desc:
      "Campanhas geolocalizadas no Meta Ads com automação pós venda para recompra.",
  },
  {
    big: "3.8×",
    label: "agendamentos gerados",
    sector: "Clínica Estética",
    desc:
      "Agente de IA no WhatsApp qualificando leads e agendando consultas automaticamente. Sem recepcionista.",
  },
];

const Cases = () => (
  <section
    id="cases"
    className="relative py-24 sm:py-32 border-t border-[rgba(197,212,181,0.08)] bg-[#080808]"
    data-testid="cases-section"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
          / Resultados
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-5 text-[34px] sm:text-5xl lg:text-[56px] leading-[1.02] tracking-[-0.02em] font-medium text-[#F5F1E8] max-w-4xl">
          Números reais.{" "}
          <span className="text-[#C5D4B5]">Resultados que o caixa sente.</span>
        </h2>
      </Reveal>

      <div className="mt-14 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {cases.map((c, i) => (
          <Reveal key={c.sector} delay={i * 70}>
            <div
              className="group relative h-full rounded-2xl border border-[rgba(197,212,181,0.12)] bg-[#0E0E0E] p-7 sm:p-8 hover:border-[rgba(197,212,181,0.4)] hover:-translate-y-1 transition-all duration-500"
              data-cursor="hover"
              data-testid={`case-card-${i}`}
            >
              <span className="mono text-[10.5px] tracking-[0.22em] uppercase text-[#F5F1E8]/55">
                Setor · {c.sector}
              </span>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-[#C5D4B5] text-[48px] sm:text-[58px] leading-none font-medium tracking-[-0.03em]">
                  {c.big}
                </span>
              </div>
              <p className="mt-3 text-[16px] text-[#F5F1E8] leading-snug max-w-xs">
                {c.label}
              </p>
              <p className="mt-5 pt-5 border-t border-[rgba(197,212,181,0.08)] text-[13.5px] text-[#F5F1E8]/60 leading-relaxed">
                {c.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>

    <div className="mt-16 border-y border-[rgba(197,212,181,0.1)] py-4 bg-[rgba(197,212,181,0.02)]">
      <Marquee
        testid="sectors-ticker"
        slow
        items={[
          "Advocacia",
          "Decoração",
          "Ótica",
          "Estética",
          "Imobiliária",
          "Fisioterapia",
          "Clínicas",
        ]}
      />
    </div>
  </section>
);

/* -------------------- Founders -------------------- */
const Founders = () => (
  <section
    id="founders"
    className="relative py-24 sm:py-32 border-t border-[rgba(197,212,181,0.08)]"
    data-testid="founders-section"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      {/* Photo */}
      <Reveal className="lg:col-span-5">
        <div className="relative">
          <div className="relative rounded-2xl overflow-hidden border border-[rgba(197,212,181,0.18)] sage-glow-soft">
            <img
              src={FOUNDERS_IMG}
              alt="Marcus e Julia — fundadores Seven Impulse"
              className="w-full h-auto object-cover"
              data-testid="founders-photo"
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, transparent 55%, rgba(10,10,10,0.55) 100%)",
              }}
            />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 border-l border-[#C5D4B5] pl-4">
              <p className="text-[#F5F1E8] text-[15px] font-medium">Marcus</p>
              <p className="mono text-[10.5px] tracking-[0.2em] uppercase text-[#F5F1E8]/55 mt-1">
                Estratégia & Performance
              </p>
            </div>
            <div className="flex-1 border-l border-[#C5D4B5] pl-4">
              <p className="text-[#F5F1E8] text-[15px] font-medium">Julia</p>
              <p className="mono text-[10.5px] tracking-[0.2em] uppercase text-[#F5F1E8]/55 mt-1">
                Automação & IA
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Copy */}
      <div className="lg:col-span-7">
        <Reveal>
          <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
            / Quem somos
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="mt-5 text-[32px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.02em] font-medium text-[#F5F1E8]">
            Por trás da tecnologia,{" "}
            <span className="text-[#C5D4B5]">tem gente de verdade.</span>
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-6 text-[15.5px] sm:text-[17px] leading-relaxed text-[#F5F1E8]/70 max-w-2xl">
            Um casal que decidiu fazer diferente e trocou o previsível pela
            obsessão de ver negócios locais crescerem de verdade. Com tráfego,
            automação e IA aplicada. Em pouco mais de um ano, mais de 34
            empresas transformadas, clientes em todo o Brasil e R$ 2.307.517
            geridos. Não por sorte. Por método.
          </p>
        </Reveal>

        <Reveal delay={240}>
          <div className="mt-10 grid grid-cols-2 gap-6 sm:gap-10 max-w-md">
            <div>
              <Counter
                value={2307517}
                duration={2200}
                format="currency-brl"
                className="block text-[#C5D4B5] text-[34px] sm:text-[44px] font-medium tracking-[-0.02em] leading-none"
                testid="counter-revenue"
              />
              <p className="mono text-[10.5px] tracking-[0.2em] uppercase text-[#F5F1E8]/55 mt-3">
                Em mídia gerida
              </p>
            </div>
            <div>
              <Counter
                value={34}
                duration={1500}
                format="plus"
                className="block text-[#C5D4B5] text-[34px] sm:text-[44px] font-medium tracking-[-0.02em] leading-none"
                testid="counter-clients"
              />
              <p className="mono text-[10.5px] tracking-[0.2em] uppercase text-[#F5F1E8]/55 mt-3">
                Empresas transformadas
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={300}>
          <div className="mt-10 h-px w-24 bg-[#C5D4B5]" />
        </Reveal>
      </div>
    </div>
  </section>
);

/* -------------------- Final CTA -------------------- */
const FinalCTA = () => (
  <section
    id="cta"
    className="relative py-24 sm:py-36 border-t border-[rgba(197,212,181,0.08)] overflow-hidden"
    data-testid="cta-section"
  >
    <div className="aurora opacity-60" aria-hidden="true" />
    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
          / Próximo passo
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-6 text-[36px] sm:text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.025em] font-medium text-[#F5F1E8]">
          Seu concorrente já está usando IA.{" "}
          <span className="text-[#C5D4B5]">E você?</span>
        </h2>
      </Reveal>
      <Reveal delay={160}>
        <p className="mt-7 text-[16px] sm:text-[18px] leading-relaxed text-[#F5F1E8]/70 max-w-2xl mx-auto">
          Uma conversa de 20 minutos para mapear as alavancas certas do seu
          negócio. Sem pitch longo. Sem enrolação.
        </p>
      </Reveal>
      <Reveal delay={240}>
        <div className="mt-10 flex flex-col items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-6 py-3.5 text-[15px] font-medium hover:brightness-95 transition sage-glow"
            data-testid="cta-button"
            data-cursor="hover"
          >
            Agendar diagnóstico gratuito
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform"
            />
          </a>
          <p className="mono text-[11px] tracking-[0.18em] uppercase text-[#F5F1E8]/50">
            Resposta em minutos · WhatsApp
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------- Footer -------------------- */
const Footer = () => (
  <footer
    className="relative border-t border-[rgba(197,212,181,0.12)] bg-[#080808] py-16"
    data-testid="footer"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-6">
          <Logo size={36} />
          <p className="mt-5 max-w-md text-[14px] text-[#F5F1E8]/55 leading-relaxed">
            Seven Impulse é parceiro de crescimento para negócios locais que
            querem performance com inteligência artificial. Brasília–DF.
          </p>
        </div>

        <div className="md:col-span-6 flex flex-col md:items-end gap-4">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-5 py-3 text-[14px] font-medium hover:brightness-95 transition w-fit"
            data-testid="footer-whatsapp-btn"
            data-cursor="hover"
          >
            <MessageSquare size={15} strokeWidth={2.4} />
            Quero vender mais
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[14px] text-[#F5F1E8]/70 hover:text-[#C5D4B5] transition w-fit"
            data-testid="footer-instagram-link"
            data-cursor="hover"
          >
            <Instagram size={16} />
            @7impulsegrowth
          </a>
        </div>
      </div>

      <div className="mt-14 pt-6 border-t border-[rgba(197,212,181,0.08)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="mono text-[10.5px] tracking-[0.22em] uppercase text-[#F5F1E8]/45">
          Marketing · IA · Automação
        </p>
        <p className="mono text-[10.5px] tracking-[0.18em] uppercase text-[#F5F1E8]/45">
          © {new Date().getFullYear()} Seven Impulse · Brasília–DF
        </p>
      </div>
    </div>
  </footer>
);

/* -------------------- Page -------------------- */
export default function Landing() {
  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-[#F5F1E8]" data-testid="landing-page">
      <CursorFX />
      <TopBar />
      <Hero />
      <Services />
      <Cases />
      <Founders />
      <FinalCTA />
      <Footer />
      <MayaChat />
    </div>
  );
}
